import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';

import {
  fetchOrderDetails,
  postOrderReceipt,
  voidOrderById,
  returnOrderById,
  reopenOrderById,
  cancelPaylinkById,
} from '../../utils/api/client';
import { getPosApiUrl, getAccessToken } from '../../utils/state';
import { replaceAlerts } from '../../components/alert-bar/AlertBar.state';
import {
  ORDER_STATUS,
  PAYMENT_CANCELLATION_STATUS,
} from '../../utils/constants';
import { createDpsVoidPolling } from '../../utils/dpsVoidPolling';
import {
  INITIATE_VOID,
  INITIATE_RETURN,
  GET_ORDER_DETAILS_REQUEST,
  SUBMIT_SEND_RECEIPT,
  SUBMIT_PRINT_RECEIPT,
  orderDetailsAlertBarAction,
  getOrderDetailsSuccess,
  voidSuccess,
  voidError,
  stopVoidOrderPolling,
  STOP_VOID_ORDER_POLLING,
  returnSuccess,
  returnError,
  sendReceiptSuccess,
  sendReceiptError,
  printReceiptSuccess,
  printReceiptError,
  voidModal,
  returnModal,
  INITIATE_REOPEN,
  reopenSuccess,
  reopenError,
  CLOSE_VOID_MODAL,
  CLOSE_RETURN_MODAL,
  INITIATE_CANCEL_PAYLINK,
  cancelPaylinkError,
  cancelPaylinkSuccess,
} from './OrderDetails.state';
import { caughtError } from '../../utils/api/errorHandler';
import { cartAlertBarAction } from '../cart/Cart.state';

export const caughtOrderDetailsErrorCallback = (state) =>
  caughtError(orderDetailsAlertBarAction, 'Order Details Error:', state);

const voidOrderByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: action.payload.id,
  notes: action.payload.notes,
  method: 'PUT',
});

const returnOrderByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: action.payload.id,
  notes: action.payload.notes,
  method: 'PUT',
});

const reopenOrderByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: action.payload.id,
  method: 'PUT',
});

export const getOrderDetailsParams = (state, payload) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: payload.id,
});

const cancelPaylinkByIdParams = (state, action) => {
  const baseUrl = getPosApiUrl(state);
  const webhookUrl = baseUrl.replace('/api/v2', '/webhooks/v1');
  return {
    url: webhookUrl,
    token: getAccessToken(state),
    id: action.payload.id,
    method: 'PATCH',
  };
};

const ORDER_VOID_INITIATION_OUTCOME = {
  PROCESSING: ORDER_STATUS.VOID_PENDING,
  VOIDED: ORDER_STATUS.VOIDED,
  UNEXPECTED: 'unexpected',
};

const classifyOrderVoidInitiationOutcome = (response) => {
  const orderStatus = response.data?.status;
  if (orderStatus === ORDER_STATUS.VOID_PENDING) {
    return ORDER_VOID_INITIATION_OUTCOME.PROCESSING;
  }
  if (
    orderStatus === ORDER_STATUS.VOIDED &&
    !response.data?.payment_cancellation_status
  ) {
    return ORDER_VOID_INITIATION_OUTCOME.VOIDED;
  }
  return ORDER_VOID_INITIATION_OUTCOME.UNEXPECTED;
};

const ORDER_VOID_POLL_OUTCOME = {
  ASSISTANCE_REQUIRED: PAYMENT_CANCELLATION_STATUS.ASSISTANCE_REQUIRED,
  VOIDED: ORDER_STATUS.VOIDED,
  UNEXPECTED_TERMINAL: 'unexpected_terminal',
};

const classifyOrderVoidPollOutcome = (pollResponse) => {
  const order = pollResponse.data?.order;
  const cancellationStatus = order?.payment_cancellation_status;
  if (
    order?.status === ORDER_STATUS.VOID_PENDING &&
    cancellationStatus === PAYMENT_CANCELLATION_STATUS.ASSISTANCE_REQUIRED
  ) {
    return ORDER_VOID_POLL_OUTCOME.ASSISTANCE_REQUIRED;
  }
  if (
    order?.status &&
    order.status !== ORDER_STATUS.VOID_PENDING &&
    cancellationStatus
  ) {
    return ORDER_VOID_POLL_OUTCOME.UNEXPECTED_TERMINAL;
  }
  if (order?.status === ORDER_STATUS.VOIDED) {
    return ORDER_VOID_POLL_OUTCOME.VOIDED;
  }
  if (order?.status && order.status !== ORDER_STATUS.VOID_PENDING) {
    return ORDER_VOID_POLL_OUTCOME.UNEXPECTED_TERMINAL;
  }
  return null;
};

export const voidByIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_VOID).pipe(
    RxOp.flatMap((action) =>
      Rx.from(voidOrderById(voidOrderByIdParams(state$.value, action))).pipe(
        RxOp.flatMap((response) => {
          const initiationOutcome =
            classifyOrderVoidInitiationOutcome(response);
          if (initiationOutcome === ORDER_VOID_INITIATION_OUTCOME.PROCESSING) {
            return Rx.concat(
              Rx.of(
                voidSuccess(response),
                voidModal(CLOSE_VOID_MODAL),
                orderDetailsAlertBarAction(
                  replaceAlerts({
                    heading: 'Void Processing',
                    text: 'Card payment void is processing...',
                    variant: 'info',
                  }),
                ),
              ),
              createDpsVoidPolling({
                pollFn: () =>
                  Rx.from(
                    fetchOrderDetails(
                      getOrderDetailsParams(state$.value, {
                        id: response.data.order.id,
                      }),
                    ),
                  ),
                isDone: (pollResponse) =>
                  Boolean(classifyOrderVoidPollOutcome(pollResponse)),
                onSuccess: (pollResponse) => {
                  switch (classifyOrderVoidPollOutcome(pollResponse)) {
                    case ORDER_VOID_POLL_OUTCOME.ASSISTANCE_REQUIRED:
                      return Rx.of(
                        getOrderDetailsSuccess(pollResponse.data),
                        orderDetailsAlertBarAction(
                          replaceAlerts({
                            heading: 'Void Assistance Required',
                            text: 'This void could not be completed automatically. You may begin a new order. This order requires assistance.',
                            variant: 'error',
                          }),
                        ),
                        stopVoidOrderPolling(),
                      );
                    case ORDER_VOID_POLL_OUTCOME.VOIDED:
                      return Rx.of(
                        getOrderDetailsSuccess(pollResponse.data),
                        orderDetailsAlertBarAction(
                          replaceAlerts({
                            heading: 'Order Voided',
                            text: 'All payments have been successfully voided and the order is now voided.',
                            variant: 'success',
                          }),
                        ),
                        stopVoidOrderPolling(),
                      );
                    default:
                      return Rx.of(
                        getOrderDetailsSuccess(pollResponse.data),
                        orderDetailsAlertBarAction(
                          replaceAlerts({
                            heading: 'Void Error',
                            text: 'Void failed. Please check the order status.',
                            variant: 'error',
                          }),
                        ),
                        stopVoidOrderPolling(),
                      );
                  }
                },
                onTimeout: () =>
                  Rx.concat(
                    Rx.from(
                      fetchOrderDetails(
                        getOrderDetailsParams(state$.value, {
                          id: response.data.order.id,
                        }),
                      ),
                    ).pipe(
                      RxOp.flatMap((pollResponse) =>
                        Rx.of(getOrderDetailsSuccess(pollResponse.data)),
                      ),
                      RxOp.catchError(() => Rx.EMPTY),
                    ),
                    Rx.of(
                      orderDetailsAlertBarAction(
                        replaceAlerts({
                          heading: 'Void Status',
                          text: 'Void is still processing. Please refresh the page to check the latest status.',
                          variant: 'warn',
                        }),
                      ),
                      stopVoidOrderPolling(),
                    ),
                  ),
                onError: (err) => {
                  console.error(
                    '[voidByIdEpic] Poll fetchOrderDetails failed:',
                    err?.message ?? 'unknown error',
                  );
                  return Rx.of(
                    orderDetailsAlertBarAction(
                      replaceAlerts({
                        heading: 'Void Status Unknown',
                        text: 'Could not verify void status. Please refresh the page.',
                        variant: 'error',
                      }),
                    ),
                    stopVoidOrderPolling(),
                  );
                },
                stopSignal$: action$.ofType(STOP_VOID_ORDER_POLLING),
              }),
            );
          }

          if (initiationOutcome === ORDER_VOID_INITIATION_OUTCOME.VOIDED) {
            return Rx.of(
              voidSuccess(response),
              orderDetailsAlertBarAction(
                replaceAlerts({
                  heading: 'Order Voided',
                  text: 'All payments have been successfully voided and the order is now voided.',
                  variant: 'success',
                }),
              ),
              voidModal(CLOSE_VOID_MODAL),
            );
          }

          return Rx.of(
            voidError('Unexpected void response status'),
            voidModal(CLOSE_VOID_MODAL),
            orderDetailsAlertBarAction(
              replaceAlerts({
                heading: 'Void Error',
                text: 'Void failed. Please check the order status.',
                variant: 'error',
              }),
            ),
          );
        }),
        RxOp.catchError((err) =>
          caughtOrderDetailsErrorCallback(state$.value)(
            err,
            voidError(err.message),
            voidModal(CLOSE_VOID_MODAL),
          ),
        ),
      ),
    ),
  );

export const returnByIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_RETURN).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        returnOrderById(returnOrderByIdParams(state$.value, action)),
      ).pipe(
        RxOp.flatMap((response) => {
          action.payload.navigate(`/pos/order/${response.data.order_id}`);
          return Rx.of(
            returnSuccess(response),
            orderDetailsAlertBarAction(
              replaceAlerts({
                heading: 'Order Refunded',
                text: `Order ${action.payload.id} has been successfully refunded`,
                variant: 'success',
              }),
            ),
            returnModal(CLOSE_RETURN_MODAL),
          );
        }),
        RxOp.catchError((err) =>
          caughtOrderDetailsErrorCallback(state$.value)(
            err,
            returnError(err.message),
            returnModal(CLOSE_RETURN_MODAL),
          ),
        ),
      ),
    ),
  );
export const reopenByIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_REOPEN).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        reopenOrderById(reopenOrderByIdParams(state$.value, action)),
      ).pipe(
        RxOp.flatMap((response) => {
          action.payload.navigate(`/pos/cart`);
          return Rx.of(
            reopenSuccess(response),
            cartAlertBarAction(
              replaceAlerts({
                heading: 'Order re-opened',
                text: `Order ${response.data.order_id} has been successfully re-opened`,
                variant: 'success',
              }),
            ),
          );
        }),
        RxOp.catchError((err) =>
          caughtOrderDetailsErrorCallback(state$.value)(
            err,
            reopenError(err.message),
          ),
        ),
      ),
    ),
  );

export const getOrderDetailsEpic = (action$, state$) =>
  action$.ofType(GET_ORDER_DETAILS_REQUEST).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        fetchOrderDetails(getOrderDetailsParams(state$.value, action.payload)),
      ).pipe(
        RxOp.flatMap((response) =>
          Rx.of(getOrderDetailsSuccess(response.data)),
        ),
        RxOp.catchError((err) =>
          caughtOrderDetailsErrorCallback(state$.value)(err),
        ),
      ),
    ),
  );

const caughtOrderReceiptErrorCallback = (state) =>
  caughtError(orderDetailsAlertBarAction, 'Order Receipt Error:', state);
const getReceiptFormInputs = (state) => {
  const receiptTypes = [];
  if (state.orderDetails.forms.receiptForm.emailReceipt.rawValue)
    receiptTypes.push('email');
  if (state.orderDetails.forms.receiptForm.textReceipt.rawValue)
    receiptTypes.push('sms');

  return {
    receipt_types: receiptTypes,
    id: state.orderDetails.data.order.id,
    email: state.orderDetails.forms.receiptForm.emailAddress.rawValue,
    phone: state.orderDetails.forms.receiptForm.phoneNumber.rawValue,
  };
};
const sendReceiptParams = (state) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  duplicate: true,
  type: 'send',
  ...getReceiptFormInputs(state),
});

export const sendReceiptEpic = (action$, state$) =>
  action$.ofType(SUBMIT_SEND_RECEIPT).pipe(
    RxOp.flatMap(() =>
      Rx.from(postOrderReceipt(sendReceiptParams(state$.value))).pipe(
        RxOp.flatMap((response) =>
          Rx.of(
            sendReceiptSuccess(response.data),
            orderDetailsAlertBarAction(
              replaceAlerts({
                heading: 'Receipt Sent',
                text: 'Receipt has been successfully sent.',
                variant: 'success',
              }),
            ),
          ),
        ),
        RxOp.catchError((err) =>
          caughtOrderReceiptErrorCallback(state$.value)(
            err,
            sendReceiptError(),
          ),
        ),
      ),
    ),
  );

const printReceiptParams = (state) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  receipt_types: ['print'],
  type: 'print',
  duplicate: true,
  id: state.orderDetails.data.order.id,
});
export const printReceiptEpic = (action$, state$) =>
  action$.ofType(SUBMIT_PRINT_RECEIPT).pipe(
    RxOp.flatMap(() =>
      Rx.from(postOrderReceipt(printReceiptParams(state$.value))).pipe(
        RxOp.flatMap((response) =>
          Rx.of(
            printReceiptSuccess(response.data),
            orderDetailsAlertBarAction(
              replaceAlerts({
                heading: 'Receipt Printed',
                text: `Receipt for order: ${response.data.order.id} has been successfully printed.`,
                variant: 'success',
              }),
            ),
          ),
        ),
        RxOp.catchError((err) =>
          caughtOrderReceiptErrorCallback(state$.value)(
            err,
            printReceiptError(),
          ),
        ),
      ),
    ),
  );
export const cancelPaylinkByIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_CANCEL_PAYLINK).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        cancelPaylinkById(cancelPaylinkByIdParams(state$.value, action)),
      ).pipe(
        RxOp.flatMap((response) => {
          return Rx.of(
            cancelPaylinkSuccess(response),
            orderDetailsAlertBarAction(
              replaceAlerts({
                heading: 'Paylink Cancelled',
                text: `Paylink Invoice for ${action.payload.order_id} has been successfully cancelled`,
                variant: 'success',
              }),
            ),
          );
        }),
        RxOp.catchError((err) =>
          caughtOrderDetailsErrorCallback(state$.value)(
            err,
            cancelPaylinkError(err.message),
          ),
        ),
      ),
    ),
  );
